const REFERRAL_STORAGE_KEY = "twoshot-referral-attribution";
const MAX_REFERRER_TAG_LENGTH = 100;

export interface ReferralAttribution {
    referrerId: number | null;
    referrerTag: string | null;
    // twos.hot short-link code (?slc) that drove this visit. Independent of the credit-bearing
    // referrer_id/tag: a casual content share carries a code but no referrer id.
    shortLinkCode: string | null;
}

const NO_REFERRAL: ReferralAttribution = {
    referrerId: null,
    referrerTag: null,
    shortLinkCode: null,
};

export function captureReferralAttributionFromUrl(): ReferralAttribution {
    const params = new URLSearchParams(window.location.search);
    const referral = normaliseReferralAttribution(
        params.get("referrer_id"),
        params.get("referrer_tag"),
        params.get("slc"),
    );
    if (hasReferral(referral)) {
        setStoredReferralAttribution(referral);
        return referral;
    }
    return getStoredReferralAttribution();
}

export function getStoredReferralAttribution(): ReferralAttribution {
    try {
        const raw = sessionStorage.getItem(REFERRAL_STORAGE_KEY);
        if (!raw) return NO_REFERRAL;
        const parsed: unknown = JSON.parse(raw);
        if (!isRecord(parsed)) return NO_REFERRAL;
        return normaliseReferralAttribution(parsed["referrerId"], parsed["referrerTag"], parsed["shortLinkCode"]);
    } catch {
        return NO_REFERRAL;
    }
}

export function referralRequestBody(): Record<string, string | number> {
    const referral = getStoredReferralAttribution();
    const body: Record<string, string | number> = {};
    if (referral.referrerId !== null) body.referrer_id = referral.referrerId;
    else if (referral.referrerTag !== null) body.referrer_tag = referral.referrerTag;
    if (referral.shortLinkCode !== null) body.slc = referral.shortLinkCode;
    return body;
}

export function appendReferralParams(params: URLSearchParams): void {
    const referral = getStoredReferralAttribution();
    if (referral.shortLinkCode !== null) params.set("slc", referral.shortLinkCode);
    if (referral.referrerId !== null) {
        params.set("referrer_id", String(referral.referrerId));
    } else if (referral.referrerTag !== null) {
        params.set("referrer_tag", referral.referrerTag);
    }
}

export function clearStoredReferralAttribution(): void {
    try {
        sessionStorage.removeItem(REFERRAL_STORAGE_KEY);
    } catch {
        /* sessionStorage may be unavailable in restricted browser modes */
    }
}

function setStoredReferralAttribution(referral: ReferralAttribution): void {
    try {
        sessionStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(referral));
    } catch {
        /* sessionStorage may be unavailable in restricted browser modes */
    }
}

function normaliseReferralAttribution(rawId: unknown, rawTag: unknown, rawSlc: unknown): ReferralAttribution {
    const referrerId = parseReferrerId(rawId);
    const referrerTag = referrerId !== null ? null : cleanReferrerTag(rawTag);
    return {referrerId, referrerTag, shortLinkCode: parseShortLinkCode(rawSlc)};
}

function parseReferrerId(value: unknown): number | null {
    if (typeof value === "string" && !/^[1-9]\d*$/.test(value.trim())) return null;
    if (typeof value !== "string" && typeof value !== "number") return null;
    const raw = typeof value === "number" ? value : Number.parseInt(value, 10);
    return Number.isSafeInteger(raw) && raw > 0 ? raw : null;
}

function cleanReferrerTag(value: unknown): string | null {
    if (typeof value !== "string") return null;
    const cleaned = value.replace(/[\u0000-\u001f\u007f]/g, "").trim().slice(0, MAX_REFERRER_TAG_LENGTH);
    return cleaned.length > 0 ? cleaned : null;
}

function parseShortLinkCode(value: unknown): string | null {
    if (typeof value !== "string") return null;
    const cleaned = value.trim();
    return /^[A-Za-z0-9]{3,16}$/.test(cleaned) ? cleaned : null;
}

function hasReferral(referral: ReferralAttribution): boolean {
    return referral.referrerId !== null || referral.referrerTag !== null || referral.shortLinkCode !== null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}
